// Gio — 공간 디자이너 탭의 클라이언트 (2.3.0). 서버는 functions/gio.js.
//
//   users/{uid}/gio/{YYYY-MM-DD}/messages   하루 한 대화. 쓰기는 서버만 (rules).
//   users/{uid}/presence/gio                보고 있는 동안 30초마다 — 서버가 45초
//                                           안이면 답장 푸시를 생략한다 (notify.js).
//                                           2.3.0 은 users/{uid}.gioViewing 에 썼다.
//
// 하루 키는 서버의 dayKey(users.timezone) 와 같아야 같은 대화를 본다.
// timezone 은 initializeUser 가 이 기기의 IANA 타임존으로 넣는다.
import { auth, db } from '../firebase.js';
import {
  collection, doc, query, orderBy, limit, limitToLast, endBefore, startAfter, onSnapshot, getDocs, setDoc, deleteDoc,
  serverTimestamp, documentId,
} from 'firebase/firestore';
import { currentLang } from '../hooks/useLocale.jsx';

const BASE = 'https://us-central1-voda-7647c.cloudfunctions.net';

export function localDayKey(d = new Date()) {
  let tz;
  try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { tz = undefined; }
  try { return new Intl.DateTimeFormat('en-CA', { timeZone: tz || 'UTC' }).format(d); }
  catch { return new Intl.DateTimeFormat('en-CA').format(d); }
}

async function post(name, data) {
  const user = auth.currentUser;
  if (!user || user.isAnonymous) throw Object.assign(new Error('AUTH_REQUIRED'), { code: 'AUTH_REQUIRED' });
  const token = await user.getIdToken();
  const res = await fetch(`${BASE}/${name}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ data }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(body.error || 'Request failed');
    err.code = body.error;
    err.status = res.status;
    if (body.requiredCredits !== undefined) err.required = body.requiredCredits;
    if (body.availableCredits !== undefined) err.available = body.availableCredits;
    throw err;
  }
  return body;
}

/** 메시지 보내기 — { text, images:[storage URL], designId?, clientId }.
 * clientId 는 메시지마다 하나 — 같은 id 로 다시 보내면 서버는 한 번만 처리하고, 저장된
 * 메시지 id 가 u_{clientId} / g_{clientId} 라 화면이 임시 말풍선을 정확히 짝지을 수 있다. */
function chat({ text = '', images = [], designId = null, clientId }) {
  return post('gioChat', { text, images, designId, clientId, lang: currentLang() });
  // → { thread, messageId, reply, actions, charged }
}

export function newClientId() {
  const a = new Uint8Array(12);
  crypto.getRandomValues(a);
  return Array.from(a, (b) => b.toString(16).padStart(2, '0')).join('');
}

/** 실행 카드의 "진행" — 카드에 방 사진이 없으면 photoUrls 를 같이 보낸다. */
// photoUrls — 카드에서 고른 사진 (카드의 사진보다 먼저). additionalRequest — 카드에서 고친 요청.
function runAction({ thread, messageId, actionId, photoUrls = [], additionalRequest }) {
  return post('gioRunAction', { thread, messageId, actionId, photoUrls, ...(additionalRequest !== undefined && { additionalRequest }), lang: currentLang() });
  // → { designId, cost } | { designId, already: true }
}

// 화면은 날짜를 넘어 이어지는 한 줄기 대화다 (Gio.jsx). 한 번에 다 부르지 않는다 —
// 최근 PAGE 개, 위로 올리면 PAGE 개씩 더, 그날이 끝나면 직전 날로.
export const PAGE = 30;
const toMsgs = (day, docs) => docs.map((d) => ({ id: d.id, thread: day, ...d.data() }));

/** 오늘 대화 — 실시간. n 을 늘리면 더 위까지. cb(messages, hasMore) */
function subscribeToday(uid, day, n, cb) {
  const q = query(collection(db, 'users', uid, 'gio', day, 'messages'), orderBy('createdAt'), limitToLast(n));
  return onSnapshot(q, (snap) => cb(toMsgs(day, snap.docs), snap.size >= n), () => cb([], false));
}

/** 지난 날 대화의 한 페이지 — cursor(그 페이지 맨 위 문서) 이전 PAGE 개. 지난 날은 고정이라 한 번 읽기. */
async function loadEarlier(uid, day, cursor = null) {
  const base = collection(db, 'users', uid, 'gio', day, 'messages');
  const q = cursor
    ? query(base, orderBy('createdAt'), endBefore(cursor), limitToLast(PAGE))
    : query(base, orderBy('createdAt'), limitToLast(PAGE));
  const snap = await getDocs(q);
  return { messages: toMsgs(day, snap.docs), hasMore: snap.size >= PAGE, cursor: snap.docs[0] || cursor };
}

/** beforeDay 직전의, 메시지가 있는 날. 문서 id 가 날짜라 id 역순으로 정렬한다 — 이 정렬에는
 * 복합 인덱스(gio, __name__ DESC)가 필요하다 (firestore.indexes.json). 없으면 null. */
async function previousDay(uid, beforeDay) {
  const snap = await getDocs(query(collection(db, 'users', uid, 'gio'), orderBy(documentId(), 'desc'), startAfter(beforeDay), limit(10)));
  return snap.docs.find((d) => (d.data().messageCount || 0) > 0)?.id || null;
}

function setViewing(uid, thread) {
  if (!uid) return Promise.resolve();
  const ref = doc(db, 'users', uid, 'presence', 'gio');
  return (thread ? setDoc(ref, { thread, at: serverTimestamp() }) : deleteDoc(ref)).catch(() => {});
}

export const GioService = { chat, runAction, subscribeToday, loadEarlier, previousDay, setViewing };
export default GioService;
